import { API_ORIGIN, apiUrl } from "./apiOrigin";
// FeedbackRatingsPublic.js
// =============================================================================
// Two copyable, no-login "star ratings" dashboard links, both served by this
// one component: "/?pfbr=TOKEN&view=org" (organisation-wide) and
// "/?pfbr=TOKEN&view=branches" (broken down by branch). Minted from the
// Patient feedback module's "Star ratings" tab — see PatientFeedback.js /
// feedback_routes.js. Same shape as PatientFeedbackPublic's "?pfb=TOKEN" and
// InspectorSummaryPublic's "?insp=TOKEN" — the token in the URL is the only
// credential, no login, no nav rail — deliberately self-contained rather
// than importing from PatientFeedback.js.
//
// Meant to be left open on a screen (a wall display, a bookmark on a
// manager's phone) — the data is re-fetched automatically every 24 hours so
// the page never needs a manual reload to stay current, and a "Refresh now"
// button is there for anyone who wants it sooner.
// =============================================================================

import React, { useCallback, useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Cell, LabelList } from "recharts";
import { QRCodeSVG } from "qrcode.react";
const API_BASE = apiUrl("");
const REFRESH_MS = 24 * 60 * 60 * 1000; // 24 hours

const S = {
  page: {
    minHeight: "100vh",
    background: "#F5F5F5",
    fontFamily: "system-ui, sans-serif",
    padding: "28px 20px"
  },
  wrap: {
    maxWidth: 900,
    margin: "0 auto"
  },
  headerCard: {
    background: "#fff",
    borderRadius: 12,
    padding: "20px 24px",
    marginBottom: 16,
    boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    flexWrap: "wrap",
    gap: 12
  },
  orgName: {
    fontSize: 20,
    fontWeight: 700,
    color: "#262626",
    margin: 0
  },
  subtitle: {
    fontSize: 13.5,
    color: "#5D5D5D",
    marginTop: 4
  },
  meta: {
    fontSize: 12,
    color: "#8C8C8C",
    marginTop: 6
  },
  refreshBtn: {
    padding: "7px 14px",
    fontSize: 12.5,
    fontWeight: 600,
    border: "1px solid #D9D9D9",
    borderRadius: 6,
    background: "#fff",
    color: "#434343",
    cursor: "pointer"
  },
  card: {
    background: "#fff",
    borderRadius: 12,
    padding: "22px 24px",
    marginBottom: 16,
    boxShadow: "0 4px 24px rgba(0,0,0,0.06)"
  },
  heroRow: {
    display: "flex",
    gap: 28,
    alignItems: "center",
    flexWrap: "wrap",
    marginBottom: 6
  },
  heroNumber: {
    fontSize: 54,
    fontWeight: 800,
    lineHeight: 1,
    color: "#262626"
  },
  heroStars: {
    fontSize: 26,
    letterSpacing: 2
  },
  heroSub: {
    fontSize: 13,
    color: "#5D5D5D",
    marginTop: 6
  },
  cardTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: "#262626",
    marginBottom: 14
  },
  err: {
    fontSize: 13,
    color: "#A8071A",
    background: "#FFF1F0",
    border: "1px solid #FFCCC7",
    borderRadius: 8,
    padding: "12px 16px"
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 13,
    marginTop: 4
  },
  th: {
    textAlign: "left",
    padding: "8px 10px",
    borderBottom: "2px solid #F0F0F0",
    fontSize: 11.5,
    textTransform: "uppercase",
    letterSpacing: 0.3,
    color: "#5D5D5D"
  },
  td: {
    padding: "8px 10px",
    borderBottom: "1px solid #F5F5F5"
  },
  reviewTitle: {
    fontWeight: 700,
    fontSize: 14.5,
    color: "#262626",
    marginBottom: 4
  },
  reviewSub: {
    fontSize: 13,
    color: "#5D5D5D",
    marginBottom: 14
  },
  select: {
    width: "100%",
    boxSizing: "border-box",
    padding: "9px 11px",
    fontSize: 13.5,
    border: "1px solid #D9D9D9",
    borderRadius: 6,
    fontFamily: "inherit",
    background: "#fff",
    color: "#262626"
  },
  qrBox: {
    background: "#fff",
    padding: 10,
    border: "1px solid #F0F0F0",
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  qrHelp: {
    fontSize: 13,
    color: "#434343",
    maxWidth: 220,
    lineHeight: 1.4
  }
};

// Status colour by rating — same green/amber/red convention used elsewhere
// in this module (e.g. the MHRA acknowledgement columns): >=4 good, >=3
// middling, below that a concern. Grey stands in for "no ratings yet".
function ratingColor(avg) {
  if (avg == null) return "#8C8C8C";
  if (avg >= 4) return "#389E0D";
  if (avg >= 3) return "#d97706";
  return "#CF1322";
}
function Stars({
  avg
}) {
  if (avg == null) return <span style={{
    color: "#8C8C8C",
    fontSize: 15
  }}>No ratings yet</span>;
  const rounded = Math.round(avg);
  return <span style={S.heroStars}>
      {[1, 2, 3, 4, 5].map(n => <span key={n} style={{
      color: n <= rounded ? "#FA8C16" : "#F0F0F0"
    }}>★</span>)}
    </span>;
}
function fmtWhen(d) {
  if (!d) return "";
  const dt = new Date(d);
  return isNaN(dt) ? "" : dt.toLocaleString("en-GB", {
    dateStyle: "medium",
    timeStyle: "short"
  });
}

// Lets anyone looking at this dashboard — org-wide or per-branch — pick
// their own pharmacy and get its actual feedback QR code on the spot,
// rather than needing to already have the poster in front of them. The
// QR itself is nothing new: it's the same "?pfb=" link that branch's
// wall poster already carries (see feedback_routes.js's feedbackUrl).
function ReviewInviteCard({
  branches
}) {
  const [selected, setSelected] = useState("");
  const branch = (branches || []).find(b => b.pharmacyName === selected);
  return <div style={S.card}>
      <div style={S.reviewTitle}>Would you like to leave a review now?</div>
      <div style={S.reviewSub}>Select your pharmacy, and scan the QR code to submit your review.</div>
      <div style={{
      display: "flex",
      gap: 24,
      alignItems: "center",
      flexWrap: "wrap"
    }}>
        <div style={{
        minWidth: 240,
        flex: "0 1 320px"
      }}>
          <select style={S.select} value={selected} onChange={e => setSelected(e.target.value)}>
            <option value="">Select your pharmacy from here..</option>
            {(branches || []).map(b => <option key={b.pharmacyName} value={b.pharmacyName}>{b.pharmacyName}</option>)}
          </select>
        </div>
        {selected && branch && branch.feedbackUrl && <div style={{
        display: "flex",
        alignItems: "center",
        gap: 14
      }}>
            <div style={S.qrBox}>
              <QRCodeSVG value={branch.feedbackUrl} size={110} />
            </div>
            <div style={S.qrHelp}>Scan this QR code and click the link that displays to leave a review.</div>
          </div>}
        {selected && (!branch || !branch.feedbackUrl) && <div style={{
        fontSize: 12.5,
        color: "#8C8C8C"
      }}>No feedback link is available for this pharmacy yet.</div>}
      </div>
    </div>;
}
function OrgView({
  data
}) {
  const dist = data.org.distribution || {};
  const chartData = [1, 2, 3, 4, 5].map(n => ({
    star: `${n} ★`,
    count: dist[n] || 0
  }));
  return <>
      <div style={S.card}>
        <div style={S.heroRow}>
          <div style={{
          ...S.heroNumber,
          color: ratingColor(data.org.avgRating)
        }}>
            {data.org.avgRating != null ? data.org.avgRating.toFixed(1) : "—"}
          </div>
          <div>
            <Stars avg={data.org.avgRating} />
            <div style={S.heroSub}>
              {data.org.totalRatings} rating{data.org.totalRatings === 1 ? "" : "s"} across the organisation
            </div>
          </div>
        </div>
      </div>
      <div style={S.card}>
        <div style={S.cardTitle}>Rating distribution</div>
        <div style={{
        width: "100%",
        height: 280
      }}>
          <ResponsiveContainer>
            <BarChart data={chartData} margin={{
            top: 10,
            right: 10,
            left: 0,
            bottom: 0
          }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F5F5F5" />
              <XAxis dataKey="star" tick={{
              fontSize: 12.5,
              fill: "#5D5D5D"
            }} axisLine={{
              stroke: "#F0F0F0"
            }} tickLine={false} />
              <YAxis allowDecimals={false} tick={{
              fontSize: 12,
              fill: "#8C8C8C"
            }} axisLine={false} tickLine={false} />
              <Tooltip formatter={v => [`${v} rating${v === 1 ? "" : "s"}`, ""]} labelStyle={{
              fontWeight: 700
            }} />
              <Bar dataKey="count" fill="#722ED1" radius={[4, 4, 0, 0]} maxBarSize={64}>
                <LabelList dataKey="count" position="top" style={{
                fontSize: 12,
                fill: "#434343",
                fontWeight: 700
              }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </>;
}
function BranchesView({
  data
}) {
  const chartData = data.branches.map(b => ({
    name: b.pharmacyName || "—",
    avgRating: b.avgRating ?? 0,
    ratingCount: b.ratingCount,
    hasData: b.avgRating != null
  }));
  const chartHeight = Math.max(220, chartData.length * 44 + 40);
  return <>
      <div style={S.card}>
        <div style={S.cardTitle}>Average star rating by branch</div>
        {chartData.length === 0 ? <div style={{
        fontSize: 13,
        color: "#5D5D5D"
      }}>No branches found.</div> : <div style={{
        width: "100%",
        height: chartHeight
      }}>
            <ResponsiveContainer>
              <BarChart data={chartData} layout="vertical" margin={{
            top: 6,
            right: 36,
            left: 10,
            bottom: 0
          }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#F5F5F5" />
                <XAxis type="number" domain={[0, 5]} tick={{
              fontSize: 12,
              fill: "#8C8C8C"
            }} axisLine={{
              stroke: "#F0F0F0"
            }} tickLine={false} />
                <YAxis type="category" dataKey="name" width={160} tick={{
              fontSize: 12.5,
              fill: "#434343"
            }} axisLine={false} tickLine={false} />
                <Tooltip formatter={(v, key, item) => key === "avgRating" ? [item.payload.hasData ? `${v.toFixed(1)} ★ (${item.payload.ratingCount} rating${item.payload.ratingCount === 1 ? "" : "s"})` : "No ratings yet", "Average rating"] : [v, key]} />
                <Bar dataKey="avgRating" radius={[0, 4, 4, 0]} maxBarSize={22}>
                  {chartData.map((row, i) => <Cell key={i} fill={ratingColor(row.hasData ? row.avgRating : null)} />)}
                  {/* LabelList's formatter only receives the raw value, not the row — and
                      avgRating is only ever 0 for a branch with no ratings at all (real
                      averages start at 1★), so that's the safe signal to show "—" instead. */}
                  <LabelList dataKey="avgRating" position="right" formatter={v => v > 0 ? v.toFixed(1) : "—"} style={{
                fontSize: 12,
                fill: "#434343",
                fontWeight: 700
              }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>}
      </div>
      <div style={S.card}>
        <div style={S.cardTitle}>By branch</div>
        <div style={{
        overflowX: "auto"
      }}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Branch</th>
                <th style={{
                ...S.th,
                textAlign: "right"
              }}>Average rating</th>
                <th style={{
                ...S.th,
                textAlign: "right"
              }}>Ratings received</th>
              </tr>
            </thead>
            <tbody>
              {data.branches.map(b => <tr key={b.pharmacyName}>
                  <td style={S.td}>{b.pharmacyName}</td>
                  <td style={{
                ...S.td,
                textAlign: "right",
                fontWeight: 700,
                color: ratingColor(b.avgRating)
              }}>
                    {b.avgRating != null ? `${b.avgRating.toFixed(1)} ★` : "—"}
                  </td>
                  <td style={{
                ...S.td,
                textAlign: "right"
              }}>{b.ratingCount}</td>
                </tr>)}
              {data.branches.length === 0 && <tr><td style={S.td} colSpan={3}>No branches found.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </>;
}
export default function FeedbackRatingsPublic({
  token,
  view
}) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setErr(null);
    try {
      const res = await fetch(`${API_BASE}/api/feedback/public/ratings?token=${encodeURIComponent(token)}`);
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || "This link isn't valid.");
      setData(body);
    } catch (e) {
      setErr(e.message);
    }
    setLoading(false);
  }, [token]);
  useEffect(() => {
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);
  const isBranches = view === "branches";
  return <div style={S.page}>
      <div style={S.wrap}>
        <div style={S.headerCard}>
          <div>
            <div style={S.orgName}>{data && data.organisationName || "Patient feedback"}</div>
            <div style={S.subtitle}>
              {isBranches ? "Star ratings by branch" : "Star rating across the organisation"}
            </div>
            {data && <div style={S.meta}>Updated {fmtWhen(data.generatedAt)} · refreshes automatically every 24 hours</div>}
          </div>
          <button style={S.refreshBtn} onClick={() => {
          setLoading(true);
          load();
        }}>Refresh now</button>
        </div>

        {loading && !data && <div style={S.card}><div style={{
          color: "#5D5D5D",
          fontSize: 13
        }}>Loading…</div></div>}
        {err && <div style={S.err}>{err}</div>}
        {data && <ReviewInviteCard branches={data.branches} />}
        {data && (isBranches ? <BranchesView data={data} /> : <OrgView data={data} />)}
      </div>
    </div>;
}
